import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import type { FetchResult } from "@apollo/client";
import { Observable } from "@apollo/client/utilities";

import type { SupabaseGraphQLClientOptions } from "./types.js";

export type SupabaseBrowserGraphQLConfig = {
  url: string;
  anonKey: string;
  /**
   * Resolve the current session's access token, or `null`/`undefined` when
   * signed out (falls back to the anon key). Injected rather than assuming a
   * specific Supabase client construction — call sites already have a
   * `supabase-js` browser client in scope in the common case:
   *
   * ```ts
   * createBrowserGraphQLClient(config, async () => {
   *   const { data } = await supabase.auth.getSession();
   *   return data.session?.access_token ?? null;
   * });
   * ```
   */
  getAccessToken: () => Promise<string | null | undefined>;
};

/**
 * Browser-side client. Resolves the auth token per-operation (not once at
 * client construction — one Apollo client instance can outlive the session
 * scope it was built in, e.g. across a sign-in), same behavior as Hub's
 * original browser client.
 */
export function createBrowserGraphQLClient(
  config: SupabaseBrowserGraphQLConfig,
  options: SupabaseGraphQLClientOptions,
): ApolloClient {
  const { jsonSerialization = true, extraHeaders, ssrMode, ...apolloOptions } = options;
  const httpLink = new HttpLink({ uri: `${config.url}/graphql/v1` });

  const authLink = new ApolloLink((operation, forward) => {
    return new Observable<FetchResult>((observer) => {
      const run = async () => {
        try {
          const token = await config.getAccessToken();
          operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
            headers: {
              ...headers,
              apikey: config.anonKey,
              Authorization: `Bearer ${token ?? config.anonKey}`,
              ...(extraHeaders?.() ?? {}),
            },
          }));

          const sub = forward(operation).subscribe({
            next: (result) => observer.next(result),
            error: (error) => observer.error(error),
            complete: () => observer.complete(),
          });
          return () => sub.unsubscribe();
        } catch (error) {
          observer.error(error);
          return undefined;
        }
      };
      void run();
    });
  });

  const links: ApolloLink[] = [];
  if (jsonSerialization) {
    links.push(
      new ApolloLink((operation, forward) => {
        if (operation.variables) {
          operation.variables = JSON.parse(JSON.stringify(operation.variables));
        }
        return forward(operation);
      }),
    );
  }
  links.push(authLink, httpLink);

  return new ApolloClient({
    ...apolloOptions,
    link: ApolloLink.from(links),
    cache: new InMemoryCache(),
    ssrMode: ssrMode ?? typeof window === "undefined",
  });
}
